/**
 * Which map you are playing.
 *
 * One so far. It is here anyway, and it is here now rather than later,
 * because the shape of a game with two maps is not the shape of a game with
 * one plus a special case: the world options, the save slot and the name on
 * the home screen all have to come from the same place, and retrofitting that
 * once a second map exists means touching every one of them under pressure.
 *
 * **A map is not a save.** Picking one decides what world is grown and where
 * it is stored; it does not load anything. Each map keeps its own live slot,
 * so starting a mini-game cannot overwrite the world somebody has been
 * building for a month - which is the one mistake this whole idea could make.
 *
 * Pure: names, seeds and sizes. Nothing here knows what a chunk is.
 */

import type { Phrase } from "../text/lang.js";

export interface GameMap {
  /** Stable id. Goes into the save-slot name, so it must never change. */
  readonly id: string;
  readonly name: Phrase;
  readonly blurb: Phrase;
  /** Chunks across, and up. */
  readonly sizeXZ: number;
  readonly sizeY: number;
  /**
   * Whether the far island grows a кремль.
   *
   * A property of the map rather than of the world generator: a mini-game on
   * a flat field has no business inheriting a fortress because the code that
   * grows one happens to run.
   */
  readonly kremlin: boolean;
  /**
   * What shape of world to grow. Islands unless the map says otherwise.
   *
   * See `WorldGenOptions.terrain` for why this is a branch rather than a
   * second generator.
   */
  readonly terrain?: "islands" | "hill" | "adventure" | "war" | "one";
  /**
   * Only worth playing with other people, so only offered with them.
   *
   * "Грати наодинці немає сенсу, тому нехай ця мапа буде доступна тільки для
   * гри в мережі." A map with this set is missing from the single-player
   * picker altogether and appears in the list of worlds somebody can put up
   * on a server.
   */
  readonly online?: boolean;
  /**
   * A match rather than a world.
   *
   * «Король гори» is one race and «Сучасна війна» is one battle: when it is
   * over there is nothing left to come back to, and a finished match sitting
   * in the list of worlds people can join is a room with the lights off.
   * "Ця мапа не має жити довше 10 хвилин, а краще видаляти одразу."
   *
   * A map with this set is not put up by the server at startup - somebody
   * makes one when they want to play - and it is taken down when the match
   * ends or when it has stood empty for ten minutes.
   */
  readonly match?: boolean;
  /**
   * Whether дядько Гриць and тітка Одарка live here.
   *
   * "Думаю на цій мапі вони взагалі не потрібні." They are the game's first
   * joke and its first 18+ content - two людей standing near where the player
   * wakes, who say one thing each when poked - and on «Запалити кремль» they
   * are two strangers on a beach in a map about burning a fortress. On
   * «Пригода», which is villages and travelling, they belong.
   *
   * Absent means nobody: a match map has no room for anybody to stand about
   * in, and a new map has to say it wants them.
   */
  readonly villagers?: boolean;
}

/**
 * Запалити кремлін: the game as it stands.
 *
 * Two islands, a causeway, and a fortress full of orc lairs on the far one.
 * The name says what there is to do - the лігва are inside it, and the only
 * way the raids stop is to go over there and put it out.
 */
export const BURN_THE_KREMLIN: GameMap = {
  id: "kremlin",
  name: { uk: "Запалити кремлін", en: "Burn the кремль" },
  blurb: {
    uk: "Два острови, коса між ними. Орки живуть у фортеці на дальньому.",
    en: "Two islands and a causeway. The orcs live in the fortress on the far one.",
  },
  sizeXZ: 12,
  sizeY: 8,
  kremlin: true,
};

/**
 * Король гори: one cube, and three blocks in ten that go off.
 *
 * Deliberately the opposite of the кремль in every way that map is a game
 * about preparation: nothing to craft, nothing to build, nothing to fight and
 * nowhere to hide. There is a hundred-block cube of identical вибухоблоки,
 * you break them to climb, and three in ten take the floor out from under
 * you. The whole map is the decision of which block to hit next.
 *
 * Eight chunks each way: a hundred and twenty-eight blocks, which leaves
 * fourteen of open floor round the hill to arrive on and to fall onto, and
 * enough headroom above it that the summit is not the ceiling of the world.
 */
export const KING_OF_THE_HILL: GameMap = {
  id: "hill",
  name: { uk: "Король гори", en: "King of the hill" },
  blurb: {
    uk: "Куб зі ста блоків. Три з десяти вибухають. Заберися нагору й лишись там.",
    en: "A cube a hundred blocks on a side. Three in ten explode. Get on top and stay there.",
  },
  sizeXZ: 8,
  sizeY: 8,
  kremlin: false,
  terrain: "hill",
  match: true,
};

/**
 * Пригода: country to walk into, rather than a map to win.
 *
 * No кремль, no lairs, no raids and nothing to burn - the opposite of the
 * other two, which are both games with an end. Meadow, mountain, winter and
 * desert, laid out by climate, and a new seed every time it is started.
 *
 * Two hundred and fifty-six chunks across against the кремль's twelve: four
 * thousand blocks, so two thousand in every direction from a start near the
 * middle. It was sixty-four, which is a thousand blocks *in total* - and a
 * player walking out from the middle met the coast at five hundred, which
 * was reported as the world having stopped generating. It had not; it had
 * ended. "Це дуже мало" is the right answer to that, and the number was
 * chosen when growing the map cost eight seconds at the title screen.
 *
 * It costs nothing now. Nothing is made until somebody goes there, the
 * memory is given back behind them, and the only thing that reads the span
 * at all is the search for a lake to start beside - which stops at the first
 * one it finds and never walks the map.
 *
 * It can be that big because none of it is made until somebody goes there.
 * The world starts with a patch about two hundred blocks across and grows a
 * couple of columns a frame towards wherever the player is walking; the
 * light is worked out per column on the same terms. The old version grew and
 * lit the whole map before the first frame - two byte arrays covering every
 * cell - which was eight seconds at twenty chunks and would be fifteen at
 * forty. That is the wall this map used to be sized by.
 *
 * Still not endless: there is a coast at the edge of it, and a save file
 * holds the whole thing. What genuinely endless needs is in NETWORK.md.
 */
export const ADVENTURE: GameMap = {
  id: "adventure",
  name: { uk: "Пригода", en: "Adventure" },
  blurb: {
    uk: "Великий світ: луки, гори, зима й пустеля. Щоразу новий.",
    en: "A wide world: meadows, mountains, winter and desert. New every time.",
  },
  sizeXZ: 256,
  sizeY: 8,
  kremlin: false,
  terrain: "adventure",
  villagers: true,
};

/**
 * Один блок: the whole world is one block and the sky round it.
 *
 * "Гравці зявляються на одному блоці а навколо порожнеча... Ціль: побудувати
 * світ з одного такого блоку." Four blows on the шопопало-блок shake
 * something out of it - any block, any thing, any creature - and the block
 * stays. Everything anybody ever builds here came out of that one cell.
 *
 * Not a match: there is nothing to win and nothing to lose, and a world
 * somebody has spent a week building out of a single block is the last thing
 * that should be swept away when the round ends. And it is offered both
 * alone and together, because both work: alone it is patience, together it
 * is four people round one block arguing about what to spend the next drop
 * on.
 *
 * Small - four chunks - because nothing is generated: what makes a world
 * here is what people put down, and a huge empty box is just further to
 * fall.
 */
export const ONE_BLOCK: GameMap = {
  id: "one",
  name: { uk: "Один блок", en: "One block" },
  blurb: {
    uk: "Один блок і порожнеча. Бий його — і з нього випаде світ.",
    en: "One block and the void. Hit it, and a world falls out.",
  },
  /*
   * Thirty-two chunks across, for a map that is one block and an island.
   *
   * "Віддали острів на 200 блоків." The block stands in the middle and the
   * island two hundred blocks east of it, so the world has to be wide
   * enough to hold both with void beyond. Chunks are made only where
   * something is written, so the void in between costs nothing.
   */
  sizeXZ: 32,
  sizeY: 8,
  kremlin: false,
  terrain: "one",
};

/**
 * Сучасна війна: протистояння, and the first map that is a *match*.
 *
 * "Пласка поверхня, росте багато дерев, під землею 60 блоків, дуже багато
 * ресурсів." Flat on purpose: nothing here is about the ground. The map is
 * two or four armies, a wall between them, ten minutes to prepare, and a
 * прапор in the middle of everybody's quarter that only адамантій opens.
 *
 * Twelve chunks: a hundred and ninety-two blocks across, so a quarter of it
 * is ninety-six on a side - a walk of twenty seconds from your flag to the
 * wall, which is the distance an evening of this wants. Sixty blocks of
 * ground under the grass and the floor of the world under that.
 */
export const MODERN_WAR: GameMap = {
  id: "war",
  name: { uk: "Сучасна війна", en: "Modern war" },
  blurb: {
    uk: "Протистояння. Рівнина, ліси, глибокі надра. Знищ прапор суперника.",
    en: "A confrontation. Flat country, woods, deep ground. Destroy the other flag.",
  },
  sizeXZ: 12,
  sizeY: 8,
  kremlin: false,
  terrain: "war",
  online: true,
  match: true,
};

export const MAPS: readonly GameMap[] =
  [BURN_THE_KREMLIN, KING_OF_THE_HILL, ADVENTURE, MODERN_WAR, ONE_BLOCK];

/** The maps that are a match: one round, and then nothing to come back to. */
export const MATCH_MAPS: readonly GameMap[] = MAPS.filter((m) => m.match === true);

/** The maps somebody can start on their own. */
export const SOLO_MAPS: readonly GameMap[] =
  MAPS.filter((m) => m.online !== true);

export const DEFAULT_MAP = BURN_THE_KREMLIN;

export function mapById(id: string | null): GameMap {
  return MAPS.find((m) => m.id === id) ?? DEFAULT_MAP;
}

/**
 * Where this map's live world is kept.
 *
 * The first map keeps the bare slot name it has always had, so nobody's
 * existing world moves out from under them; every map after it is suffixed.
 * That asymmetry is ugly and is the price of not renaming a slot somebody is
 * standing in.
 */
export function liveSlotFor(map: GameMap, liveSlot: string): string {
  return map.id === DEFAULT_MAP.id ? liveSlot : `${liveSlot}.${map.id}`;
}
